import React, { useCallback, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useOnClickOutside } from "utils/hooks/ui";
import HoverIconButton from "components/HoverIconButton";
import classnames from "classnames";
import { FORM_KEY } from "utils/constants/form";
import { useAppVariables } from "utils/selectors";
import Image from "components/Image";
import orderBy from "lodash/orderBy";
import { matchSorter } from "match-sorter";
import { AUTO_ATTRS } from "components/Form/fields/constants";
import debounce from "lodash/debounce";
import get from "lodash/get";
import { getIconForUID } from "components/FormerEditor/utils";
import InfoNotice from "components/InfoNotice";
import { ensureValidVariableSample } from "utils/variable";
import { VARIABLE_MODEL } from "utils/constants/variable";
import { COLORS } from "utils/styles";

const VARIABLE_SERVICE_ICON_DIMENSIONS = {
  width: 28,
  height: 28
};

const IconNode = ({ uid }) => {
  const Icon = getIconForUID(uid);
  return (
    <div
      style={{
        width: 18,
        height: 18
      }}
      className={
        "flex items-center justify-center bg-content--secondary br2 icon--boundary flex-shrink-0"
      }
    >
      <Icon size={12} color={COLORS.white} />
      <style jsx>{`
        .icon--boundary {
          background-color: ${COLORS.primary};
        }
      `}</style>
    </div>
  );
};

const VariableMenu = ({ setShowMenu, editor, name }) => {
  const ref = useRef();
  const searchRef = useRef(null);
  const [search, setSearch] = useState(null);
  const { values } = useFormikContext();
  const field = get(values, name);

  const { variables } = values[FORM_KEY];
  const hideMenu = useCallback(() => setShowMenu(false), []);
  useOnClickOutside(ref, hideMenu);

  const appVariables = useAppVariables();
  const allVariables = [...variables, ...appVariables.static];

  const orderedVariables = useMemo(() =>
    orderBy(allVariables, ["service", "label"], ["asc"], [])
  );

  const onUpdate = (query) => {
    setSearch(query);
  };

  const debounceOnChange = useMemo(() => debounce(onUpdate, 200), []);
  const SearchIcon = getIconForUID("search");

  const inputProps = {
    ref: searchRef,
    className:
      "f5 border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20 ",
    type: "text",
    name: "text",
    onChange: (evt) => debounceOnChange(evt.currentTarget.value),
    ...AUTO_ATTRS,
    placeholder: "Search..."
  };
  const clearSearch = () => {
    setSearch(null);
    if (searchRef && searchRef.current) {
      searchRef.current.value = "";
    }
  };

  const filtered = search
    ? matchSorter(orderedVariables, search, {
        keys: ["label"]
      })
    : orderedVariables;
  const filteredCount = filtered.length;

  let results;
  if (filteredCount) {
    const searchNote = search
      ? `${filteredCount} matches`
      : `${filteredCount} of ${filteredCount}`;
    results = (
      <div className="w-100">
        <ul
          style={{ maxHeight: 420 }}
          className="list ma0 pa0 overflow-y-scroll"
        >
          {filtered.map((variable, varIx) => {
            const sample = ensureValidVariableSample({ field, variable });

            let iconNode;
            if (
              variable.model === VARIABLE_MODEL.CHECKOUT_FORM_DATA &&
              variable.modelUid
            ) {
              iconNode = <IconNode uid={variable.modelUid} />;
            }

            return (
              <li
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .insertContent({
                      type: "mention",
                      attrs: variable
                    })
                    .insertContent(" ") // add an extra space after the mention
                    .run();
                }}
                key={variable.uuid}
                className={classnames("w-100 pointer bg-white--hover pa2", {
                  "bb hairline-1": varIx !== orderedVariables.length - 1
                })}
              >
                <div className="flex flex-row w-100 items-center">
                  <div
                    style={VARIABLE_SERVICE_ICON_DIMENSIONS}
                    className="br2 pa1 hairline-1 ba overflow-hidden"
                  >
                    {iconNode || (
                      <Image
                        src={`/assets/integrations/${variable.service}/icon.png`}
                        {...VARIABLE_SERVICE_ICON_DIMENSIONS}
                        alt={variable.service}
                      ></Image>
                    )}
                  </div>
                  <div className="f5 pl2 fw5">{variable.label}</div>
                  <div className="f6 pl2 black-60">{sample}</div>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="pa2 w-100 flex flex-row justify-between items-center bt hairline-1">
          <div className="f6">{searchNote}</div>
        </div>
      </div>
    );
  } else {
    results = (
      <div className="w-100 pointer" onClick={clearSearch}>
        <InfoNotice
          copy="No search matches, click to clear."
          customClasses={{
            container: "w-100 flex flex-row pa2 items-center"
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{ marginTop: 8 }}
      className="f5 absolute z-1 left-0 right-0 bg-white ba hairline-1 br2 shadow-0"
    >
      <div className="w-100 flex flex-row bb hairline-1">
        <div className="pa2 flex flex-row justify-between items-center w-100">
          <div className="f5 fw6">Insert data</div>
          <HoverIconButton type="close" onClick={() => setShowMenu(false)} />
        </div>
      </div>
      <div className="pl2 pv2 pr3 bb hairline-1 flex flex-row items-center">
        <SearchIcon size={18} />
        <div className="pl2 w-100">
          <input {...inputProps} />
        </div>
      </div>
      {results}
    </div>
  );
};

export default VariableMenu;
